/*
 * Autosuggest component for use in app selection.  This is intended to replace the direct use of the MUI autosuggest component
 * used in several pages.
 * 
 * This takes the following props:
 *   addSubscription         - parent function to add a subscription
 *   addAuthOnlySubscription - parent function to add an auth-only subscription
 *   usingAccountId          - the using account id (customer, or reseller for self sub)
 *   usingAccountName        - the using account name (customer scenario)
 *   provisioningAccountId   - the provisioning account id (reseller)
 *   setDisableNext          - parent function to set state of next button
 *   defaultApp              - default app to select in the edge case that we're to navigate directly to an app
 *                             TODO: implement
 *   placeholder             - if set, use this text as the placeholder for the suggestion field
 *   suggestDefaults         - if true, prepopulate with defaults.
 *   autoFocus               - if true, autoFocus on the suggestion field
 * 
 * We could in the future take props to only select certain kinds of apps (e.g. we thought about acceptMode having SO_paid,
 * SO_authOnly, and SO_all, but decided to leave that for later).
 * 
 * We could also take a prop for the component style to emit, but in this iteration are only returning a table row intended
 * for use in the calling compenent's table.
 */

import React from 'react';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';
import withStyles from '@mui/styles/withStyles';

import { Button, Grid, MenuItem, Paper, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';

import ModelPicker from './ModelPicker';
import AppLogo from '../ui/AppLogo';
import SavingSpinner from '../ui/SavingSpinner';
import AppFetch from '../app/AppFetch';
import AppDefaultFetch from '../app/AppDefaultFetch'

const abEnvironment = new AbEnvironment();

function renderInputComponent(inputProps) {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;
   
    return (
        <TextField
            fullWidth
            InputProps={{
                inputRef: node => {
                    ref(node);
                    inputRef(node);
                },
                classes: {
                    input: classes.input,
                },
            }}
            {...other}
        />
    );
}

function renderInputComponentAutoFocus(inputProps) {
    const { classes, inputRef = () => {}, ref, ...other } = inputProps;
   
    return (
        <TextField
            fullWidth
            autoFocus={true}
            InputProps={{
                inputRef: node => {
                    ref(node);
                    inputRef(node);
                },
                classes: {
                    input: classes.input,
                },
            }}
            {...other}
        />
    );
}

/**
 * Render a suggestion for an app name.
 * @param {*} suggestion 
 * @param {*} param1 
 * @returns 
 */
function renderAppSuggestion(suggestion, { query, isHighlighted }) {
    const matches = match(suggestion.appName, query);
    const parts = parse(suggestion.appName, matches);
    
    // Is this template app or a vendor app?  
    // It is a template if it is owned by the reseller.
    // Need to distinguish between template and
    // new.
    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');
    let isTemplate = false;
    if (suggestion.accountId === provisioningAccountId) {
        if (suggestion.appId === 'customapp') {
            isTemplate = false;
        } else {
            isTemplate = true;
        }
    }
    
    let skuPart = "";
    if (suggestion.appSku != null && suggestion.appSku !== '' && suggestion.appSku !== suggestion.appName) {
        skuPart = ' (' + suggestion.appSku + ')';
    }
   
    let appMessage = "";
    let ital = false;
    if (isTemplate) {
        appMessage = "";
    } else {
        ital = true;
        appMessage = "+ Add " + suggestion.appName;
    }
   
    return (
        <MenuItem selected={isHighlighted} component="div">
            <Grid container spacing={2} direction="row" justifyContent="flex-start">
                <Grid item xs={1}>
                    <AppLogo logoPath={suggestion.appLogo} appName={suggestion.appName} imageHeight='24px'/>
                </Grid>
                <Grid item xs={6}>
                    {/* We set the search on appname above - so that is what
                        is in the part variable. */}
                    <Typography variant="subtitle1">
                        {parts.map((part, index) => {
                            if (part.highlight && suggestion.appLogo != null) {
                                return (<strong key={index}>{part.text}</strong>);
                            } else {
                                return (part.text);
                            }
                        })}
                        {skuPart}
                    </Typography>
                </Grid>
                <Grid item xs={5}>
                    {ital &&
                    <Typography variant="subtitle1">
                        <i>{appMessage}</i>
                    </Typography>}
                    {! ital &&
                    <Typography variant="subtitle1">
                        {appMessage}
                    </Typography>}
                </Grid>
            </Grid>
        </MenuItem>
    );
}

const getSuggestionValue = suggestion => suggestion.appName;

//these two needed to make warnings go away in autoselect.  Should not be the case according to doc.
function getSectionSuggestions(section) {
    return null;
}
   
function renderSectionTitle(section) {
    return null;
}
   
const styleSheet = theme => ({
    root: {
      flexGrow: 1,
    },
    container: {
      position: 'relative',
    },
    suggestionsContainerOpen: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0,
    },
    suggestion: {
      display: 'block',
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyleType: 'none',
    },
    divider: {
      height: theme.spacing(2),
    },
    autoSuggestContainer: {
      marginBottom: theme.spacing(1),
    },
    appRow: {
      paddingTop: 0,
    },
    appCellLeft: {
      paddingLeft: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      width: 48,
      border: 0,
    },
    appCell: {
        fontSize: 14,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        minWidth: 64,
        border: 0,
    },
    appCellPaperContainer: {
        paddingTop: 0,
        padding: 0,
        border: 0,
    },
    appCellPaper: {
        padding: theme.spacing(2),
        borderTopRightRadius: "0px",
        borderTopLeftRadius: "0px",
        borderBottomRightRadius: "0px",
        borderBottomLeftRadius: "0px",
    },
    appDetailCell: {
        fontSize: 14,
        paddingTop: 0,
        paddingBottom: theme.spacing(1),
        minWidth: 64,
        border: 0,
    },
    appCellRight: {
        fontSize: 14,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        width: 64,
        border: 0,
    },
    appCellWhole: {
        paddingLeft: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        border: 0,
    },
    appTextField: {
        marginTop: theme.spacing(1),
    },
    tabroot: {
        flexGrow: 1,
        margin: 0,
        width: '100%',
        borderBottom: '2px solid #dedede'
    },
    tabScrollButton: {
        flex: '0 0 0px'
    },
    addButton: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    cancelButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    }
});
  
class AppAutosuggest extends React.Component {
    state = {
        showSpinner: false,

        suggestions: [],
        searchP: '',
        appRowHeight: 40,

        cApp: {
            appId: null,
            accountId: null,
            appStatus: null,
            parentAppId: null,
            oemAppId: null,
            appName: null,
            appSku: null,
            appUrl: null,
            signupUrl: null,
            appLogo: null,
            description: null,
            appType: null,
            atId: null,
            appSkuPrice: null,
            appMarkupAbsolute: null,
            appMarkupPercentage: 0,
            marqetaMid: null,
            marqetaMname: null,
            maxCharge: null,
            paymentOrder: null,
            passThroughFees: null,
            wholesaleDiscountPercentage: 0,
            showDomain: null,
            showUsername: null,
            showSubscription: null,
            notes: null, // Customer App only
            appBindAddress: null,
            taskType: null,
            taskDataAuth: null,
            taskDataInvite: null,
            instructionTitle: null,
            instructionText: null,
            linkUrl: null,
            linkName: null
        },

        // temp values for use in save disable
        paymentOrder: null,
        wholesaleDiscountPercentage: 0,
        appMarkupPercentage: 0,
    };

    /**
     * If a default app is passed in, set the state to that app.
     */
    componentDidMount() {
        if (this.props.app) {
            let theApp = this.props.app;

            let appName = theApp.appName;
            if (appName.length > 64) {
              appName = appName.substring(0, 64)
            }
        
            // Setup the new capp...
            let paymentOrder = theApp.paymentOrder;
            if (paymentOrder === 1) {
              paymentOrder = 0;
            }
            if (paymentOrder == null) {
              paymentOrder = 4;
            }

            // If expensing is not enabled, default to a non-expense paymentOrder
            let expensingActive = false;
            let expensing = abEnvironment.getFlagValue("expensing");
            if (expensing != null && expensing.flagValue === 1) {
              expensingActive = true;
            }
            
            if (expensingActive === false) {
                if (paymentOrder === 0 || paymentOrder === 2) {
                    paymentOrder = 4;
                }
            }

            let notesval = theApp.notes;
            if (notesval == null)
              notesval = '';
        
            let nobj = {
              appId: theApp.appId,
              accountId: theApp.accountId,
              appStatus: theApp.appStatus,
              parentAppId: theApp.parentAppId,
              oemAppId: theApp.oemAppId,
              appName: appName,
              appSku: theApp.appSku,
              appUrl: theApp.appUrl,
              signupUrl: theApp.signupUrl,
              appLogo: theApp.appLogo,
              description: theApp.description,
              appType: theApp.appType,
              atId: theApp.atId,
              appSkuPrice: theApp.appSkuPrice,
              appMarkupAbsolute: theApp.appMarkupAbsolute,
              appMarkupPercentage: theApp.appMarkupPercentage,
              marqetaMid: theApp.marqetaMid,
              marqetaMname: theApp.marqetaMname,
              maxCharge: theApp.maxCharge,
              paymentOrder: paymentOrder,
              passThroughFees: theApp.passThroughFees,
              wholesaleDiscountPercentage: theApp.wholesaleDiscountPercentage,
              showSubscription: 1,
              showDomain: theApp.showDomain,
              showUsername: theApp.showUsername,  
              notes: notesval,
              appBindAddress: theApp.appBindAddress,
              useEmail: theApp.useEmail,
              taskType: theApp.taskType,
              taskDataAuth: theApp.taskDataAuth,
              taskDataInvite: theApp.taskDataInvite,
              instructionTitle: theApp.instructionTitle,
              instructionText: theApp.instructionText,
              linkUrl: theApp.linkUrl,
              linkName: theApp.linkName
            }
        
            this.setState({cApp: nobj, wholesaleDiscountPercentage: theApp.wholesaleDiscountPercentage, appMarkupPercentage: theApp.appMarkupPercentage});
        }
    }

    /**
     * Function for adding a subscription.  This calls the parent prop addSubscription
     * or addAuthOnlySubscription function, passing the cApp state.
     */
    addSubscription = () => {
        this.setState({showSpinner: true});

        // Get state vals from the model picker
        if (this.theModelPicker != null) {
            // Copy the cApp from state
            let theCApp = structuredClone(this.state.cApp);

            let childState = this.theModelPicker.returnStateValuesToParent();

            if (childState.paymentOrder != null) { 
                theCApp.paymentOrder = childState.paymentOrder;
                // console.log('paymentOrder=' + theCApp.paymentOrder);
            }

            if (childState.paymentOrder === 4 || childState.paymentOrder === 5) {
                // Only add a task for payment order 4
                let taskData = {};
                if (childState.paymentOrder === 4) {
                    if (childState.taskType != null) { 
                        theCApp.taskType = childState.taskType;
                    }

                    if (childState.taskType === 0) {
                        taskData = {
                            email: childState.askForEmail,
                            password: childState.askForPassword,
                            username: childState.askForUsername,
                            domain: childState.askForDomain,
                            phone: childState.askForPhone
                        }
                    } else if (childState.taskType === 1) {
                        taskData = {
                            inviteEmails: childState.inviteEmails
                        }

                        // Set link values on cApp
                        if (childState.linkName != null) { 
                            theCApp.linkName = childState.linkName;
                        }
                        if (childState.linkUrl != null && childState.linkUrl.length > 0) { 
                            theCApp.linkUrl = childState.linkUrl;
                            if (childState.linkName == null || childState.linkName.length === 0) {
                                theCApp.linkName = 'Instructions';
                            }
                        }
                        if (childState.instructionTitle != null) { 
                            theCApp.instructionTitle = childState.instructionTitle;
                        }
                        if (childState.instructionText != null) { 
                            theCApp.instructionText  = childState.instructionText;
                        }
                    }
                } else {
                    // clear out task type if it exists
                    theCApp.taskType = null;
                }
                this.props.addAuthOnlySubscription(theCApp, taskData);
            } else {                
                if (childState.wholesaleDiscountPercentage != null) { 
                    theCApp.wholesaleDiscountPercentage = childState.wholesaleDiscountPercentage;
                }

                // App markup percentage is more interesting.  Use the rValue from ModelPicker.  The cApp
                // doesn't have an rValue - this is a new subscription - but the ModelPicker marks changes
                // on its own internal rstate.
                if (childState.appMarkupPercentage != null) { 
                    theCApp.appMarkupPercentage = childState.appMarkupPercentage;
                }
                this.props.addSubscription(theCApp);
            }
        }
    }

    /**
     * Function for cancelling adding a subscription. 
     */
    cancelAddSubscription = () => {
        this.setState({
            searchP: '',
            cApp: {
                appId: null,
                accountId: null,
                appStatus: null,
                parentAppId: null,
                oemAppId: null,
                appName: null,
                appSku: null,
                appUrl: null,
                signupUrl: null,
                appLogo: null,
                description: null,
                appType: null,
                atId: null,
                appSkuPrice: null,
                appMarkupAbsolute: null,
                appMarkupPercentage: 0,
                wholesaleDiscountPercentage: 0,
                marqetaMid: null,
                marqetaMname: null,
                maxCharge: null,
                paymentOrder: null,
                passThroughFees: null,
                showSubscription: null,
                notes: null, // Customer App only
                appBindAddress: null,
                taskType: null,
                taskDataAuth: null,
                taskDataInvite: null,
                instructionTitle: null,
                instructionText: null,
                linkUrl: null,
                linkName: null
            },
            paymentOrder: null,
            wholesaleDiscountPercentage: 0,
            appMarkupPercentage: 0
        });

        if (this.props.setDisableNext) {
            this.props.setDisableNext(false);
        }
    }

    /**
     * Sort the list of apps to be displayed.  Apps can include templates (from provisioner) and vendor apps.
     * 
     * @param {*} provisioningAccountId 
     * @param {*} searchParm 
     * @param {*} thenodes 
     */
    sortAppList(provisioningAccountId, searchParm, thenodes) {
        let theApps = [];
        let templateApps = [];
        let vendorApps = [];
        let adx = 0;
        let exactMatch = false;
    
        // Instead of foreach, loop through using an index so that
        // we can break after showing a set number of choices.  To make
        // things more complex, as we're not showing vendor apps for which
        // a template exists, we'll iterate multiple times.
        for (let i=0; i<thenodes.length; i++)
        {
            let n = thenodes[i];
            // If it is a template app, add it.
            if (n.accountId === provisioningAccountId) {
                if (n.appName === this.state.searchP) {
                    exactMatch = true;
                }
                templateApps.push(n);
            }
        }
    
        // Now non-template apps...
        for (let i=0; i<thenodes.length; i++)
        {
            let n = thenodes[i];
    
            // If it is not a template app, check if in templates.
            if (n.accountId !== provisioningAccountId)
            {
                var matchInTemplates = templateApps.find(function(anApp) {
                    return (anApp.appUrl === n.appUrl &&
                            anApp.appLogo === n.appLogo)
                });
    
                if (! matchInTemplates)
                {
                    let anApp = {
                        // vApp: 1, // Indicate that this is a vendor app.  Hack to avoid showing sku
                        appId: n.appId, // Set a default app id
                        accountId: n.accountId,
                        appStatus: n.appStatus,
                        parentAppId: n.parentAppId,
                        oemAppId: n.oemAppId,
                        appName: n.appName,
                        appSku: n.appSku, // Set a default sku
                        appUrl: n.appUrl,
                        signupUrl: n.signupUrl,
                        appLogo: n.appLogo,
                        description: n.description, //Set a default description
                        appType: n.appType,
                        atId: n.atId,
                        appSkuPrice: n.appSkuPrice,
                        appMarkupAbsolute: n.appMarkupAbsolute,
                        appMarkupPercentage: n.appMarkupPercentage,
                        passThroughFees: n.passThroughFees,
                        marqetaMid: n.marqetaMid,
                        marqetaMname: n.marqetaMname,
                        maxCharge: n.maxCharge,
                        paymentOrder: (n.paymentOrder === 1) ? 0 : n.paymentOrder,
                        wholesaleDiscountPercentage: n.wholesaleDiscountPercentage,
                        showDomain: n.showDomain,
                        showUsername: n.showUsername,
                        notes: '',
                        taskType: 0,
                        taskDataAuth: {},
                        taskDataInvite: {},
                        instructionTitle: '',
                        instructionText: '',
                        linkUrl: '',
                        linkName: ''
                    };

                    if (n.appName === this.state.searchP) {
                        exactMatch = true;
                    }
                    vendorApps.push(anApp);
                }
            }
        }
    
        // Now put these together
        let concatApps = templateApps.concat(vendorApps);
    
        // Sort them
        concatApps.sort(function(a, b) {
            a = a.appName;
            b = b.appName;
            return a>b ? 1 : a<b ? -1: 0; 
        });
    
        // Add whatever they are typing if non-blank and it does not exactly match another app
        if (this.state.searchP !== '' && !exactMatch) {
            var custApp = {
                appId: 'customapp', // Set a default app id
                accountId: provisioningAccountId,
                appStatus: 0,
                parentAppId: null,
                oemAppId: null,
                appName: searchParm,
                appSku: '', // Set a default sku
                appUrl: '',
                signupUrl: '',
                appLogo: null,
                description: '', //Set a default description
                appType: 0,
                atId: 'f5c70f86-135a-11e8-bb45-97ef56920c02',
                appSkuPrice: 0.00,
                appMarkupAbsolute: 0.00,
                appMarkupPercentage: 0,
                marqetaMid: null,
                marqetaMname: null,
                maxCharge: 1000.00,
                paymentOrder: null,
                passThroughFees: 1,
                wholesaleDiscountPercentage: 0,
                showDomain: 0,
                showUsername: 0,
                notes: '',
                useEmail: '',
                taskType:  0,
                taskDataAuth: {},
                taskDataInvite: {},
                instructionTitle: '',
                instructionText: '',
                linkUrl: '',
                linkName: ''
            };
    
            theApps.push(custApp);
        }
    
        // Put top 10 into output array
        for (let i=0; i<concatApps.length; i++)
        {
            theApps.push(concatApps[i]);
            adx++;
            if (adx >= 10)
                break;
        }
    
        this.setSuggestionState(theApps);
    }

    /*
     * Lookup apps for selection.  The lookup will return both template and vendor apps.  Unlike
     * usage in the app catalog, don't show vendor apps if they appear in a template.
     */
    fetchApps = (searchParm) => {
        // Provisioning account id
        let provisioningAccountId = this.props.provisioningAccountId;

        if (!searchParm || searchParm === '') {
            if (this.props.suggestDefaults === true) {
                AppDefaultFetch.fetch(provisioningAccountId, 0).toPromise()
                .then(adf => {
                    this.sortAppList (provisioningAccountId, searchParm, adf.appsearchsuggestedapps2.nodes);
                })
                .catch(err => {
                    this.setState({suggestions: []});
                });
            } else {
                this.setSuggestionState([]);
            }
        } else {
            AppFetch.fetch(provisioningAccountId, searchParm, 0, 50).toPromise()
            .then(af => {
                this.sortAppList (provisioningAccountId, searchParm, af.appsearchallavailablebyappname2.nodes);
            })
            .catch(err => {
                this.setSuggestionState([]);
            })
        }
    }
    
    handleSuggestionEntry = name => event => {
        if (name === 'searchP') {
            this.setState({searchP: event.target.value});
        } 
    };

    setSuggestionState = (suggestions) => {
        // Calculate height based on number of suggestions
        let height = 40 + (suggestions.length * 36);
        this.setState({suggestions: suggestions, appRowHeight: height});
    }

    setStateFromSelection(theApp) {
        //do not allow an appname > 64!
        let appName = theApp.appName;
        if (appName.length > 64) {
            appName = appName.substring(0, 64)
        }
        
        let paymentOrder = theApp.paymentOrder;

        if (paymentOrder === 1) {
          paymentOrder = 0;
        }
        if (paymentOrder == null) {
          paymentOrder = 4;
        }

        // If expensing is not enabled, default to a non-expense paymentOrder
        let expensingActive = false;
        let expensing = abEnvironment.getFlagValue("expensing");
        if (expensing != null && expensing.flagValue === 1) {
          expensingActive = true;
        }
        
        if (expensingActive === false) {
            if (paymentOrder === 0 || paymentOrder === 2) {
                paymentOrder = 4;
            }
        }

        let notesval = theApp.notes;
        if (notesval == null)
            notesval = '';
        
        let nobj = {
            appId:                       theApp.appId,
            accountId:                   theApp.accountId,
            appStatus:                   theApp.appStatus,
            parentAppId:                 theApp.parentAppId,
            oemAppId:                    theApp.oemAppId,
            appName:                     appName,
            appSku:                      theApp.appSku,
            appUrl:                      theApp.appUrl,
            signupUrl:                   theApp.signupUrl,
            appLogo:                     theApp.appLogo,
            description:                 theApp.description,
            appType:                     theApp.appType,
            atId:                        theApp.atId,
            appSkuPrice:                 theApp.appSkuPrice,
            appMarkupAbsolute:           theApp.appMarkupAbsolute,
            appMarkupPercentage:         theApp.appMarkupPercentage,
            marqetaMid:                  theApp.marqetaMid,
            marqetaMname:                theApp.marqetaMname,
            maxCharge:                   theApp.maxCharge,
            paymentOrder:                paymentOrder,
            passThroughFees:             theApp.passThroughFees,
            wholesaleDiscountPercentage: theApp.wholesaleDiscountPercentage,
            showDomain:                  theApp.showDomain,
            showUsername:                theApp.showUsername,
            showSubscription:            1,
            notes:                       notesval,
            taskType:                    0,
            taskDataAuth:                {},
            taskDataInvite:              {},
            instructionTitle:            '',
            instructionText:             '',
            linkUrl:                     '',
            linkName:                    ''
        }
        
        this.setState({cApp: nobj, paymentOrder: paymentOrder});
        if (this.props.setDisableNext) {
            this.props.setDisableNext(true);
        }
    }

    onSuggestionsFetchRequested = ({ value }) => {
        this.fetchApps(value);
    }
        
    onSuggestionsClearRequested = () => {
        this.setSuggestionState([]);
    }
    
    onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) =>{
        this.setStateFromSelection(suggestion);
    }

    shouldAutoRender = (value) => {
        return true;
    }

    getErrorMessage(field, minValue, maxValue) {
        let errMesg = null;
    
        let value = Number(field); 
        if (value < minValue) {
            return 'Cannot be less than ' + minValue;
        }
        if (value > maxValue) {
            return 'Cannot be greater than ' + maxValue;
        }
    
        return errMesg;
    }

    areFieldsInError = () => {
        if (this.getErrorMessage(this.state.appMarkupPercentage, -100, 900) !== null) {
            return true;
        }
        if (this.getErrorMessage(this.state.wholesaleDiscountPercentage, 0, 100) !== null) {
            return true;
        }
        return false;
    }

    updateModel = (paymentOrder) => {
        this.setState({paymentOrder: paymentOrder});
    }
    updateWdp = (wdp) => {
        this.setState({wholesaleDiscountPercentage: wdp});
    }
    updateAmp = (amp) => {
        this.setState({appMarkupPercentage: amp});
    }
    
    isSaveSubscriptionDisabled() {
        if (this.areFieldsInError() === true) {
            return true;
        }

        let paymentOrder = this.state.paymentOrder;
        if (paymentOrder == null) {
            return true;
        }

        let appName = this.state.cApp.appName;
        if (appName == null || appName.length === 0) {
            return true;
        }

        return false;
    }
        
    render() {
        const classes = this.props.classes;

        // 
        // In this iteration, this component is only used for selecting all kinds of apps,
        // so we aren't taking a prop to indicate paid only, auth only, or all.
        //
        // - If a template/vendor app is selected, show logo, name, sku desc, and billing
        // - If a custom app is selected, show generic logo, appname field, and billing
        //
        let showApp = false;
        let showCustom = false;
        if (this.state.cApp.appId != null) {
            if (this.state.cApp.appId === 'customapp')
                showCustom = true;
            else
                showApp = true;
        }

        let inpValue = this.state.searchP;
        if (inpValue == null)
            inpValue = '';

        let placeholder = 'Add a subscription to the project';
        if (this.props.placeholder) {
            placeholder = this.props.placeholder;
        }

        if (showApp === false && showCustom === false) {
            return (
                <TableRow>
                    <TableCell colSpan={6} style={{verticalAlign: 'top', height: this.state.appRowHeight, border: 0}}>
                        {this.props.autoFocus &&
                        <Autosuggest
                            renderInputComponent={renderInputComponentAutoFocus}
                            suggestions={this.state.suggestions}
                            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                            onSuggestionSelected={this.onSuggestionSelected}
                            getSuggestionValue={getSuggestionValue}
                            renderSuggestion={renderAppSuggestion}
                            shouldRenderSuggestions={this.shouldAutoRender}
                            multiSection={false}
                            renderSectionTitle={renderSectionTitle}
                            getSectionSuggestions={getSectionSuggestions}
                            inputProps={{
                                classes,
                                placeholder: placeholder,
                                value: inpValue,
                                onChange: this.handleSuggestionEntry('searchP'),
                            }}
                            theme={{
                                container: classes.container,
                                suggestionsContainerOpen: classes.suggestionsContainerOpen,
                                suggestionsList: classes.suggestionsList,
                                suggestion: classes.suggestion,
                            }}
                            renderSuggestionsContainer={options => (
                                <Paper {...options.containerProps} square>
                                    {options.children}
                                </Paper>
                            )}
                        />}
                        {! this.props.autoFocus &&
                        <Autosuggest
                            renderInputComponent={renderInputComponent}
                            suggestions={this.state.suggestions}
                            onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                            onSuggestionSelected={this.onSuggestionSelected}
                            getSuggestionValue={getSuggestionValue}
                            renderSuggestion={renderAppSuggestion}
                            shouldRenderSuggestions={this.shouldAutoRender}
                            multiSection={false}
                            renderSectionTitle={renderSectionTitle}
                            getSectionSuggestions={getSectionSuggestions}
                            inputProps={{
                                classes,
                                placeholder: placeholder,
                                value: inpValue,
                                onChange: this.handleSuggestionEntry('searchP'),
                            }}
                            theme={{
                                container: classes.container,
                                suggestionsContainerOpen: classes.suggestionsContainerOpen,
                                suggestionsList: classes.suggestionsList,
                                suggestion: classes.suggestion,
                            }}
                            renderSuggestionsContainer={options => (
                                <Paper {...options.containerProps} square>
                                    {options.children}
                                </Paper>
                            )}
                        />}
                        <Search color="disabled" style={{float: "right", marginTop: -26}}/>
                    </TableCell>
                </TableRow>
            );
        }

        //
        // Falling through here means that an app has been selected
        //

        //
        // Payment order needs some adjustment.
        // - If it is null, set it to empty string, which we'll use as the value for "not set", and indicate that it
        //   needs selection.
        //
        // Treat payment order 1 as 0
        //
        let thePaymentOrder = this.state.cApp.paymentOrder;
        if (thePaymentOrder === 1) {
            thePaymentOrder = 0;
        } else if (thePaymentOrder == null) {
            thePaymentOrder = 'NA';
        }

        let theInviteEmails = [];
        let accountEmails = [];
        if (this.props.inviteEmails != null && this.props.inviteEmails.length > 0) {
            for (let i=0; i<this.props.inviteEmails.length; i++) {
                let anEmail = this.props.inviteEmails[i];
                accountEmails.push(anEmail);
            }
            accountEmails.sort();
        }

        // Now setup the array to use for the checkboxes
        for (let i=0; i<accountEmails.length; i++) {
            let anEmail = accountEmails[i];
            let anEmailObj = {email: anEmail, checked: false};
            if (anEmail === this.props.defaultEmail) {
                anEmailObj.checked = true;
            }
            theInviteEmails.push(anEmailObj);
        }

        let instructionTitle = '';
        let instructionText = '';
        let linkName = '';
        let linkUrl = '';

        // Set defaults on username and domain from app
        let theAskForUsername = false;
        if (this.state.cApp.showUsername === 1) {
            theAskForUsername = true;
        }
        let theAskForDomain = false;
        if (this.state.cApp.showDomain === 1) {
            theAskForDomain = true;
        }

        let theAppModel = {
            appName:                     this.state.cApp.appName,
            paymentOrder:                thePaymentOrder,
            taskType:                    this.state.cApp.taskType,
            wholesaleDiscountPercentage: this.state.cApp.wholesaleDiscountPercentage,
            appMarkupPercentage:         this.state.cApp.appMarkupPercentage,
            instructionTitle:            instructionTitle,
            instructionText:             instructionText,
            linkName:                    linkName,
            linkUrl:                     linkUrl,             
            askForEmail:                 true,
            askForPassword:              true,
            askForUsername:              theAskForUsername,
            askForDomain:                theAskForDomain,
            askForPhone:                 false,
            inviteEmails:                theInviteEmails
        };

        return (
            <TableRow className={classes.appRow}>
                <TableCell className={classes.appCellPaperContainer} colSpan={this.props.colSpan}>
                    <Paper className={classes.appCellPaper} elevation={12}>
                        <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                            <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item >
                                        <AppLogo logoPath={this.state.cApp.appLogo} appName={this.state.cApp.appName} imageHeight='24px'/>
                                    </Grid>
                                    <Grid item>
                                        <Typography>{this.state.cApp.appName}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <ModelPicker
                                    auth={this.props.auth}
                                    mode="add"
                                    appModel={theAppModel}
                                    updateModel={this.updateModel}
                                    updateWdp={this.updateWdp}
                                    updateAmp={this.updateAmp}
                                    ref={(theModelPicker) => this.theModelPicker = theModelPicker}
                                />
                            </Grid>
                        
                            <Grid item xs={12} style={{marginTop: 8}}>
                                {this.state.saveStatus !== 'saving' &&
                                <React.Fragment>
                                    <Button
                                        className={classes.addButton}
                                        color="primary" variant="contained"
                                        onClick={() => this.addSubscription()}
                                        disabled={this.isSaveSubscriptionDisabled()}
                                    >
                                        Add subscription
                                    </Button>
                                    <Button
                                        className={classes.cancelButton}
                                        color="primary" variant="outlined"
                                        onClick={() => this.cancelAddSubscription()}
                                    >
                                        Cancel
                                    </Button>
                                </React.Fragment>}
                                {this.state.saveStatus === 'saving' &&
                                <Button disabled={true} color="primary" variant="contained" >
                                    <SavingSpinner/>
                                </Button>}
                            </Grid>

                        </Grid>
                    </Paper>
                </TableCell>
            </TableRow>
        );
    }
}

AppAutosuggest.propTypes = {
    colSpan: PropTypes.number.isRequired,
    addSubscription: PropTypes.func.isRequired,
    addAuthOnlySubscription: PropTypes.func.isRequired,
    usingAccountId: PropTypes.string.isRequired,
    usingAccountName: PropTypes.string.isRequired,
    provisioningAccountId: PropTypes.string.isRequired,
    setDisableNext: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
    suggestDefaults: PropTypes.bool.isRequired,
  };

export default withStyles(styleSheet)(AppAutosuggest);
